import { Controller } from "@hotwired/stimulus"

const DEFAULT_HIDE_DELAY_MS = 50
const DEFAULT_Z_INDEX = 100
const DURATION_BUFFER_MS = 50
const ENTER_DURATION_MS = 150
const LEAVE_DURATION_MS = 100
const GAP = 8
const OPEN_EVENT_NAME = "popover-component:open"
const VIEWPORT_MARGIN = 8
const ARROW_SIZE = 6
const ARROW_MIN_INSET = 12
const OPPOSITE = { bottom: "top", left: "right", right: "left", top: "bottom" }

const ENTER_ACTIVE_CLASSES = ["transition-opacity", "duration-150", "ease-out"]
const ENTER_FROM_CLASSES = ["opacity-0"]
const ENTER_TO_CLASSES = ["opacity-100"]
const LEAVE_ACTIVE_CLASSES = ["transition-opacity", "duration-100", "ease-in"]
const LEAVE_FROM_CLASSES = ["opacity-100"]
const LEAVE_TO_CLASSES = ["opacity-0"]
const TRANSITION_CLASSES = [
  ...new Set([
    ...ENTER_ACTIVE_CLASSES,
    ...ENTER_FROM_CLASSES,
    ...ENTER_TO_CLASSES,
    ...LEAVE_ACTIVE_CLASSES,
    ...LEAVE_FROM_CLASSES,
    ...LEAVE_TO_CLASSES,
  ]),
]

export default class extends Controller {
  static targets = ["anchor", "arrow", "content"]
  static values = {
    placement: { default: "bottom", type: String },
  }

  beforeCache = () => {
    this.hideImmediately()
    this.restoreContent()
  }

  handleAnchorClick = () => {
    this.ignoreNextAnchorFocusIn = false
    this.toggle()
  }

  handleAnchorPointerDown = () => {
    this.ignoreNextAnchorFocusIn = true
  }

  handleDocumentClick = (event) => {
    if (!this.isOpen || this.isWithinPopover(event.target)) return

    this.hide()
  }

  handleDocumentScroll = () => {
    if (!this.isOpen) return

    this.hideImmediately()
  }

  handlePopoverOpen = (event) => {
    if (event.detail?.popoverId === this.contentElement.id) return

    this.hideImmediately()
  }

  handleRegionFocusIn = (event) => {
    this.cancelHide()
    if (this.isAnchorEvent(event) && this.ignoreNextAnchorFocusIn) {
      this.ignoreNextAnchorFocusIn = false
      return
    }

    if (!this.isAnchorEvent(event)) return

    this.show()
  }

  handleRegionFocusOut = (event) => {
    if (this.isWithinPopover(event.relatedTarget)) return

    this.scheduleHide()
  }

  handleRegionMouseEnter = (event) => {
    this.cancelHide()
    if (!this.isAnchorEvent(event)) return

    this.show()
  }

  handleRegionMouseLeave = (event) => {
    if (this.isWithinPopover(event.relatedTarget)) return

    this.scheduleHide()
  }

  handleWindowKeyDown = (event) => {
    if (event.key !== "Escape") return

    this.hide()
  }

  handleWindowResize = () => {
    if (!this.isOpen) return

    this.hideImmediately()
  }

  connect() {
    if (!this.hasAnchorTarget || !this.hasContentTarget) {
      throw new Error("PopoverComponent requires anchor and content targets")
    }

    this.anchorElement = this.anchorTarget
    this.arrowElement = this.hasArrowTarget ? this.arrowTarget : null
    this.contentElement = this.contentTarget
    this.hideTimeoutId = undefined
    this.ignoreNextAnchorFocusIn = false
    this.isOpen = false
    this.openStateListenersConnected = false
    this.originalNextSibling = this.contentElement.nextSibling
    this.originalParent = this.contentElement.parentElement
    this.transitionTimeoutId = undefined

    this.validateAnchorElement()
    this.initializeAccessibility()
    this.addInteractionListeners()
    this.portalContent()
    this.hideImmediately()
    document.addEventListener(OPEN_EVENT_NAME, this.handlePopoverOpen)
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    if (!this.contentElement || !this.anchorElement) return

    this.cancelHide()
    this.cancelTransition()
    this.removeInteractionListeners()
    this.removeOpenStateListeners()
    this.hideImmediately()
    this.restoreContent()
    document.removeEventListener(OPEN_EVENT_NAME, this.handlePopoverOpen)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  addInteractionListeners() {
    this.anchorElement.addEventListener("pointerdown", this.handleAnchorPointerDown)
    this.anchorElement.addEventListener("click", this.handleAnchorClick)

    for (const element of this.interactiveElements()) {
      element.addEventListener("focusin", this.handleRegionFocusIn)
      element.addEventListener("focusout", this.handleRegionFocusOut)
      element.addEventListener("mouseenter", this.handleRegionMouseEnter)
      element.addEventListener("mouseleave", this.handleRegionMouseLeave)
    }
  }

  addOpenStateListeners() {
    if (this.openStateListenersConnected) return

    document.addEventListener("click", this.handleDocumentClick)
    document.addEventListener("scroll", this.handleDocumentScroll, true)
    window.addEventListener("keydown", this.handleWindowKeyDown)
    window.addEventListener("resize", this.handleWindowResize)
    this.openStateListenersConnected = true
  }

  applyArrow(arrow) {
    if (!this.arrowElement || !arrow) return

    Object.assign(this.arrowElement.style, { bottom: "", left: "", right: "", top: "" })
    this.arrowElement.style[arrow.edgeProperty] = arrow.edgeValue
    this.arrowElement.style[arrow.offsetProperty] = `${arrow.offsetValue}px`
    this.arrowElement.className = `absolute z-10 size-3 rotate-45 bg-white ${arrow.borderClasses}`
  }

  applyGeometry(geometry) {
    this.contentElement.style.left = `${Math.round(geometry.position.left)}px`
    this.contentElement.style.top = `${Math.round(geometry.position.top)}px`
    this.applyArrow(geometry.arrow)
  }

  basePosition(placement, anchorRect, contentRect) {
    switch (placement) {
      case "bottom":
        return {
          left: anchorRect.left + anchorRect.width / 2 - contentRect.width / 2,
          top: anchorRect.bottom + GAP,
        }
      case "left":
        return {
          left: anchorRect.left - contentRect.width - GAP,
          top: anchorRect.top + anchorRect.height / 2 - contentRect.height / 2,
        }
      case "right":
        return {
          left: anchorRect.right + GAP,
          top: anchorRect.top + anchorRect.height / 2 - contentRect.height / 2,
        }
      case "top":
        return {
          left: anchorRect.left + anchorRect.width / 2 - contentRect.width / 2,
          top: anchorRect.top - contentRect.height - GAP,
        }
    }
  }

  cancelHide() {
    if (typeof this.hideTimeoutId !== "number") return

    clearTimeout(this.hideTimeoutId)
    this.hideTimeoutId = undefined
  }

  cancelTransition() {
    if (typeof this.transitionTimeoutId !== "number") return

    clearTimeout(this.transitionTimeoutId)
    this.transitionTimeoutId = undefined
  }

  candidatePlacements(preferred) {
    if (preferred === "top" || preferred === "bottom") {
      return [preferred, OPPOSITE[preferred], "right", "left"]
    }

    return [preferred, OPPOSITE[preferred], "bottom", "top"]
  }

  clearTransitionClasses() {
    this.contentElement.classList.remove(...TRANSITION_CLASSES)
  }

  clamp(value, min, max) {
    return Math.max(min, Math.min(value, max))
  }

  clampedPosition(placement, position, contentRect) {
    const maxLeft = window.innerWidth - contentRect.width - VIEWPORT_MARGIN
    const maxTop = window.innerHeight - contentRect.height - VIEWPORT_MARGIN

    if (placement === "top" || placement === "bottom") {
      return {
        left: this.clamp(position.left, VIEWPORT_MARGIN, maxLeft),
        top: position.top,
      }
    }

    return {
      left: position.left,
      top: this.clamp(position.top, VIEWPORT_MARGIN, maxTop),
    }
  }

  contentId() {
    return `popover-component-content-${Math.random().toString(36).slice(2, 11)}`
  }

  dispatchOpenEvent() {
    document.dispatchEvent(
      new CustomEvent(OPEN_EVENT_NAME, {
        detail: { popoverId: this.contentElement.id },
      })
    )
  }

  geometryForPlacement(placement, anchorRect, contentRect) {
    const position = this.clampedPosition(
      placement,
      this.basePosition(placement, anchorRect, contentRect),
      contentRect
    )
    const arrow = this.arrowGeometry(placement, anchorRect, position, contentRect)

    return {
      arrow,
      fitsInViewport: this.fitsInViewport(placement, position, contentRect),
      placement,
      position,
    }
  }

  hide() {
    this.cancelHide()
    if (!this.isOpen) return

    this.isOpen = false
    this.removeOpenStateListeners()
    this.updateAccessibilityState()
    this.runLeaveTransition(() => {
      this.contentElement.classList.add("hidden")
      this.updateAccessibilityState()
    })
  }

  hideImmediately() {
    this.cancelHide()
    this.cancelTransition()
    this.clearTransitionClasses()
    this.contentElement.classList.add("hidden")
    this.isOpen = false
    this.removeOpenStateListeners()
    this.updateAccessibilityState()
  }

  initializeAccessibility() {
    if (!this.contentElement.id) {
      this.contentElement.id = this.contentId()
    }

    this.anchorElement.setAttribute("aria-controls", this.contentElement.id)
    this.anchorElement.setAttribute("aria-expanded", "false")
    this.anchorElement.setAttribute("aria-haspopup", "dialog")

    if (!this.contentElement.hasAttribute("role")) {
      this.contentElement.setAttribute("role", "dialog")
    }

    this.updateAccessibilityState()
  }

  interactiveElements() {
    return [this.anchorElement, this.contentElement]
  }

  isAnchorEvent(event) {
    return event.currentTarget === this.anchorElement
  }

  isInteractiveAnchorElement() {
    const tagName = this.anchorElement.tagName

    if (tagName === "A") return this.anchorElement.hasAttribute("href")

    return ["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(tagName)
  }

  isWithinPopover(target) {
    if (!target) return false

    return this.anchorElement.contains(target) || this.contentElement.contains(target)
  }

  fitsInViewport(placement, position, contentRect) {
    switch (placement) {
      case "bottom":
        return position.top + contentRect.height <= window.innerHeight - VIEWPORT_MARGIN
      case "left":
        return position.left >= VIEWPORT_MARGIN
      case "right":
        return position.left + contentRect.width <= window.innerWidth - VIEWPORT_MARGIN
      case "top":
        return position.top >= VIEWPORT_MARGIN
    }
  }

  placeContentInBody() {
    if (this.contentElement.parentElement === document.body) return

    document.body.appendChild(this.contentElement)
  }

  placeContentInWrapper() {
    if (!this.originalParent) return
    if (this.contentElement.parentElement !== document.body) return

    if (this.originalNextSibling && this.originalParent.contains(this.originalNextSibling)) {
      this.originalParent.insertBefore(this.contentElement, this.originalNextSibling)
      return
    }

    this.originalParent.appendChild(this.contentElement)
  }

  portalContent() {
    this.placeContentInBody()
    this.setPositionStyles()
  }

  positionContent() {
    const wasHidden = this.contentElement.classList.contains("hidden")

    if (wasHidden) {
      this.contentElement.classList.remove("hidden")
      this.contentElement.style.left = `${VIEWPORT_MARGIN}px`
      this.contentElement.style.top = `${VIEWPORT_MARGIN}px`
      this.contentElement.style.visibility = "hidden"
    }

    const anchorRect = this.anchorElement.getBoundingClientRect()
    const contentRect = this.contentElement.getBoundingClientRect()

    this.applyGeometry(this.resolvePlacement(anchorRect, contentRect))

    if (wasHidden) {
      this.contentElement.classList.add("hidden")
      this.contentElement.style.visibility = ""
    }
  }

  removeInteractionListeners() {
    this.anchorElement.removeEventListener("pointerdown", this.handleAnchorPointerDown)
    this.anchorElement.removeEventListener("click", this.handleAnchorClick)

    for (const element of this.interactiveElements()) {
      element.removeEventListener("focusin", this.handleRegionFocusIn)
      element.removeEventListener("focusout", this.handleRegionFocusOut)
      element.removeEventListener("mouseenter", this.handleRegionMouseEnter)
      element.removeEventListener("mouseleave", this.handleRegionMouseLeave)
    }
  }

  removeOpenStateListeners() {
    if (!this.openStateListenersConnected) return

    document.removeEventListener("click", this.handleDocumentClick)
    document.removeEventListener("scroll", this.handleDocumentScroll, true)
    window.removeEventListener("keydown", this.handleWindowKeyDown)
    window.removeEventListener("resize", this.handleWindowResize)
    this.openStateListenersConnected = false
  }

  resetPositionStyles() {
    this.contentElement.style.left = ""
    this.contentElement.style.top = ""
    this.contentElement.style.visibility = ""
    this.contentElement.style.zIndex = ""
  }

  resolvePlacement(anchorRect, contentRect) {
    const preferred = this.placementValue

    for (const placement of this.candidatePlacements(preferred)) {
      const geometry = this.geometryForPlacement(placement, anchorRect, contentRect)

      if (geometry.fitsInViewport && geometry.arrow.fits) {
        return geometry
      }
    }

    return this.geometryForPlacement(preferred, anchorRect, contentRect)
  }

  restoreContent() {
    this.placeContentInWrapper()
    this.resetPositionStyles()
  }

  runEnterTransition() {
    this.runTransition({
      activeClasses: ENTER_ACTIVE_CLASSES,
      afterStart: () => {
        this.contentElement.classList.remove("hidden")
        this.updateAccessibilityState()
      },
      duration: ENTER_DURATION_MS,
      fromClasses: ENTER_FROM_CLASSES,
      toClasses: ENTER_TO_CLASSES,
    })
  }

  runLeaveTransition(onComplete) {
    this.runTransition({
      activeClasses: LEAVE_ACTIVE_CLASSES,
      duration: LEAVE_DURATION_MS,
      fromClasses: LEAVE_FROM_CLASSES,
      onComplete,
      toClasses: LEAVE_TO_CLASSES,
    })
  }

  runTransition({
    activeClasses,
    afterStart = () => {},
    duration,
    fromClasses,
    onComplete = () => {},
    toClasses,
  }) {
    this.cancelTransition()
    this.clearTransitionClasses()
    afterStart()
    this.contentElement.classList.add(...activeClasses, ...fromClasses)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.contentElement.classList.remove(...fromClasses)
        this.contentElement.classList.add(...toClasses)

        this.transitionTimeoutId = setTimeout(() => {
          this.transitionTimeoutId = undefined
          this.contentElement.classList.remove(...activeClasses, ...toClasses)
          onComplete()
        }, duration + DURATION_BUFFER_MS)
      })
    })
  }

  scheduleHide() {
    this.cancelHide()

    this.hideTimeoutId = setTimeout(() => {
      this.hide()
      this.hideTimeoutId = undefined
    }, DEFAULT_HIDE_DELAY_MS)
  }

  setPositionStyles() {
    this.contentElement.style.zIndex = DEFAULT_Z_INDEX
  }

  show() {
    this.cancelHide()

    if (this.isOpen) {
      this.positionContent()
      this.updateAccessibilityState()
      return
    }

    this.dispatchOpenEvent()
    this.isOpen = true
    this.positionContent()
    this.addOpenStateListeners()
    this.updateAccessibilityState()
    this.runEnterTransition()
  }

  toggle() {
    if (this.isOpen) {
      this.hide()
      return
    }

    this.show()
  }

  updateAccessibilityState() {
    this.anchorElement.setAttribute("aria-expanded", this.isOpen ? "true" : "false")
    this.contentElement.setAttribute(
      "aria-hidden",
      this.contentElement.classList.contains("hidden") ? "true" : "false"
    )
  }

  validateAnchorElement() {
    if (this.isInteractiveAnchorElement()) return

    throw new Error(
      "PopoverComponent anchor target must be a real interactive element like <button> or <a href>."
    )
  }

  arrowGeometry(placement, anchorRect, position, contentRect) {
    const anchorCenterX = anchorRect.left + anchorRect.width / 2
    const anchorCenterY = anchorRect.top + anchorRect.height / 2

    if (placement === "top" || placement === "bottom") {
      const maxOffset = contentRect.width - ARROW_MIN_INSET - ARROW_SIZE * 2
      const rawOffset = anchorCenterX - position.left - ARROW_SIZE

      return {
        borderClasses: placement === "bottom" ? "border-l border-t" : "border-r border-b",
        edgeProperty: placement === "bottom" ? "top" : "bottom",
        edgeValue: `${-ARROW_SIZE}px`,
        fits: rawOffset >= ARROW_MIN_INSET && rawOffset <= maxOffset,
        offsetProperty: "left",
        offsetValue: Math.round(this.clamp(rawOffset, ARROW_MIN_INSET, maxOffset)),
      }
    }

    const maxOffset = contentRect.height - ARROW_MIN_INSET - ARROW_SIZE * 2
    const rawOffset = anchorCenterY - position.top - ARROW_SIZE

    return {
      borderClasses: placement === "right" ? "border-l border-b" : "border-r border-t",
      edgeProperty: placement === "right" ? "left" : "right",
      edgeValue: `${-ARROW_SIZE}px`,
      fits: rawOffset >= ARROW_MIN_INSET && rawOffset <= maxOffset,
      offsetProperty: "top",
      offsetValue: Math.round(this.clamp(rawOffset, ARROW_MIN_INSET, maxOffset)),
    }
  }
}
